package ai.wagemix.logic

/*
 * How analytics names a route (planning/analytics-plan.md §5, screen views). A route is reported by its pattern and a
 * stable screen name, never by the URL it was reached with: ids travel as their own property, and query strings
 * (Firebase action codes, Stripe Checkout session ids, the auth workflow) are never reported.
 */

/**
 * The route pattern of [path], OpenPanel's Pages report key: `/mix/abc` is `/mix/[id]`, `/s/mix/x` is
 * `/s/mix/[id]`. The query string, the fragment and a trailing slash are ignored. A path the site does not route is
 * `/[unknown]`, so that nothing a visitor typed reaches the report.
 *
 * @param path a path, optionally with a query string and fragment (`/auth-action?mode=verifyEmail`)
 * @return the pattern
 */
fun WMRoutes.patternOf(path: String): String {
    val routePath = routePathOf(path)
    val segments = routePath.split('/').filter { it.isNotEmpty() }
    return when {
        segments.size == 2 && segments[0] == MIX_SEGMENT -> MIX_PATTERN
        segments.size == 3 && segments[0] == SHARE_SEGMENT && segments[1] == MIX_SEGMENT -> SHARED_MIX_PATTERN
        isStaticRoute(routePath) -> routePath
        else -> UNKNOWN_PATTERN
    }
}

/**
 * The stable screen name of [path] (`mix_builder`, `shared_mix`, …), the `screen` property of every analytics event;
 * `unknown` for a path the site does not route.
 *
 * @param path a path, optionally with a query string and fragment
 * @return the screen name
 */
fun WMRoutes.screenOf(path: String): String {
    return when (patternOf(path)) {
        HOME -> "landing"
        MIX, MIX_PATTERN -> "mix_builder"
        MIXES -> "saved_mixes"
        JOB_DESCRIPTIONS -> "job_descriptions"
        ACCOUNT -> "account"
        PRICING -> "pricing"
        LOGIN -> "login"
        REGISTER -> "register"
        FORGOT_PASSWORD -> "forgot_password"
        AUTH_ACTION -> "auth_action"
        PAYMENT_RESULT -> "payment_result"
        SHARED_MIX_PATTERN -> "shared_mix"
        METHODOLOGY -> "methodology"
        TERMS -> "terms"
        PRIVACY_POLICY -> "privacy"
        LOGOUT -> "logout"
        else -> "unknown"
    }
}

/**
 * The id of the entity [path] shows: the mix id of `/mix/{id}` and the shared mix id of `/s/mix/{id}`, or null for
 * every other route.
 *
 * @param path a path, optionally with a query string and fragment
 * @return the id, or null
 */
fun WMRoutes.entityIdOf(path: String): String? {
    val segments = routePathOf(path).split('/').filter { it.isNotEmpty() }
    return when (patternOf(path)) {
        MIX_PATTERN -> segments[1]
        SHARED_MIX_PATTERN -> segments[2]
        else -> null
    }
}

/**
 * What an `/auth-action` link does, from its `mode` parameter: `verify_email` or `reset_password`, or null for any
 * other mode and any other route. The action code (`oobCode`) is never read.
 *
 * @param path the path with its query string (`/auth-action?mode=resetPassword&oobCode=…`)
 * @return the mode, or null
 */
fun WMRoutes.authActionModeOf(path: String): String? {
    if (patternOf(path) != AUTH_ACTION) {
        return null
    }
    val query = path.substringBefore('#').substringAfter('?', missingDelimiterValue = "")
    val mode = query.split('&').firstOrNull { it.startsWith("mode=") }?.substringAfter('=')
    return when (mode) {
        "verifyEmail" -> "verify_email"
        "resetPassword" -> "reset_password"
        else -> null
    }
}

private const val MIX_SEGMENT = "mix"
private const val SHARE_SEGMENT = "s"
private const val MIX_PATTERN = "/mix/[id]"
private const val SHARED_MIX_PATTERN = "/s/mix/[id]"
private const val UNKNOWN_PATTERN = "/[unknown]"
private const val METHODOLOGY = "/methodology"
private const val TERMS = "/terms"
private const val PRIVACY_POLICY = "/privacy-policy"
private const val LOGOUT = "/logout"

/** [path] without its query string, fragment and trailing slash; `/` for the root. */
private fun routePathOf(path: String): String {
    val withoutQuery = path.substringBefore('#').substringBefore('?')
    return withoutQuery.trimEnd('/').ifEmpty { WMRoutes.HOME }
}

private fun isStaticRoute(routePath: String): Boolean {
    return when (routePath) {
        WMRoutes.HOME,
        WMRoutes.MIX,
        WMRoutes.MIXES,
        WMRoutes.JOB_DESCRIPTIONS,
        WMRoutes.ACCOUNT,
        WMRoutes.PRICING,
        WMRoutes.LOGIN,
        WMRoutes.REGISTER,
        WMRoutes.FORGOT_PASSWORD,
        WMRoutes.AUTH_ACTION,
        WMRoutes.PAYMENT_RESULT,
        METHODOLOGY,
        TERMS,
        PRIVACY_POLICY,
        LOGOUT -> true
        else -> false
    }
}
